import React, { useEffect, useState, useCallback } from "react";
import { Card, Button, Badge, Row, Col, Form, Spinner, Modal, Alert } from "react-bootstrap";
import PageWrapperForm from "../components/PageWrapperForm";
import { paidService } from "../service/paidService";
import { websitePlanService } from "../service/websitePlanService";

const STORAGE_PURCHASED_KEY = "mokshiq_purchased_service_ids";

const getLocalPurchasedIds = () => {
  try {
    const saved = localStorage.getItem(STORAGE_PURCHASED_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    return [];
  }
};

const setLocalPurchasedIds = (ids) => {
  try {
    localStorage.setItem(STORAGE_PURCHASED_KEY, JSON.stringify(ids));
  } catch (e) {}
};

const PurchaseServices = () => {
  const [services, setServices] = useState([]);
  const [plansMap, setPlansMap] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlanFilter, setSelectedPlanFilter] = useState("ALL");
  const [loading, setLoading] = useState(false);

  // Purchased Service IDs state
  const [purchasedIds, setPurchasedIds] = useState(getLocalPurchasedIds());

  // Purchase Modal state
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  // Success Modal state
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await websitePlanService.getAll();
      if (res?.data && Array.isArray(res.data)) {
        const map = {};
        res.data.forEach((p) => {
          const duration = p.duration ? ` (${p.duration}M)` : "";
          map[p.id] = `${p.plan_name}${duration}`;
        });
        setPlansMap(map);
      }
    } catch (e) {
      console.warn("Could not fetch website plans list:", e);
    }
  }, []);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paidService.getAll();
      const list = Array.isArray(res?.data) ? res.data : [];
      setServices(list);
    } catch (err) {
      console.warn("Paid services endpoint not returning data yet:", err);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
    fetchServices();
  }, [fetchPlans, fetchServices]);

  const handleOpenPurchase = (item) => {
    const price = parseFloat(item.amount || 0);
    const disc = parseFloat(item.discount || 0);
    const finalPrice = price - (price * disc) / 100;
    
    setSelectedService({
      ...item,
      finalPrice: finalPrice.toFixed(2),
    });
    setErrorMessage(null);
    setShowPurchaseModal(true);
  };

  const handleConfirmPurchase = async () => {
    if (!selectedService) return;
    setPurchasing(true);
    setErrorMessage(null);

    try {
      const res = await paidService.purchaseService(selectedService);
      if (res && res.status !== false) {
        // Record purchased service ID
        const updatedPurchased = Array.from(new Set([...purchasedIds, String(selectedService.id)]));
        setPurchasedIds(updatedPurchased);
        setLocalPurchasedIds(updatedPurchased);

        // Record transaction entry for Payment History
        const newTxnLog = {
          planName: selectedService.name,
          month: 1,
          noOfDays: 30,
          paymentType: "Paid Service Wallet",
          sleepDays: 0,
          sleepPenalty: 0,
          price: selectedService.amount || selectedService.finalPrice,
          gst: 0,
          pre: 0,
          post: selectedService.finalPrice,
          purchaseDate: new Date().toLocaleDateString("en-IN").replace(/\//g, "-"),
          activeDate: new Date().toLocaleDateString("en-IN").replace(/\//g, "-"),
          renewalDate: "-",
          status: "Success",
          transaction_id: res.data?.transaction_id || `TXN_${Date.now()}`,
        };

        try {
          const existingHistory = JSON.parse(localStorage.getItem("mokshiq_paid_service_history") || "[]");
          localStorage.setItem("mokshiq_paid_service_history", JSON.stringify([newTxnLog, ...existingHistory]));
        } catch (e) {}

        setSuccessData(res.data || {
          service_name: selectedService.name,
          amount_paid: selectedService.finalPrice,
          transaction_id: `TXN_${Date.now()}`,
          purchase_date: new Date().toLocaleDateString("en-IN"),
        });
        setShowPurchaseModal(false);
        setPurchaseSuccess(true);
      } else {
        setErrorMessage(res?.message || "Failed to complete purchase. Please try again.");
      }
    } catch (err) {
      setErrorMessage(err.message || "An error occurred during purchase processing.");
    } finally {
      setPurchasing(false);
    }
  };

  const filteredServices = services.filter((s) => {
    const planName = plansMap[s.website_plan] || s.plan_name || s.website_plan || "";
    const matchesSearch =
      (s.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      planName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPlan =
      selectedPlanFilter === "ALL" || String(s.website_plan) === String(selectedPlanFilter);

    return matchesSearch && matchesPlan;
  });

  return (
    <main>
      <div className="container-fluid">
        {/* Custom Header Layout */}
        <div className="row m-1 mb-4">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <div>
              <h4 className="main-title fw-bold" style={{ color: "#1a1a1a", fontSize: "1.8rem", marginBottom: "4px" }}>Purchase Services</h4>
              <div className="d-flex align-items-center gap-2 mb-0" style={{ fontSize: "13.5px", fontWeight: "500" }}>
                <a href="#" className="text-decoration-none" style={{ color: "#7e57c2", display: "flex", alignItems: "center" }}>
                  <i className="ph-duotone ph-list-dashes f-s-16"></i> <span className="ms-1">Purchase</span>
                </a>
                <span style={{ color: "#a0a5b1" }}>/</span>
                <a href="#" className="text-decoration-none" style={{ color: "#7e57c2" }}>
                  Services
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="row m-1">
          <div className="col-12">
      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "16px" }}>
        <Card.Body className="p-4">
          <Row className="g-3 align-items-center">
            <Col md={6} sm={12}>
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Search by service name or plan..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ borderRadius: "10px", paddingLeft: "15px" }}
                />
              </div>
            </Col>
            <Col md={4} sm={8}>
              <Form.Select
                value={selectedPlanFilter}
                onChange={(e) => setSelectedPlanFilter(e.target.value)}
                style={{ borderRadius: "10px" }}
              >
                <option value="ALL">All Website Plans</option>
                {Object.entries(plansMap).map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2} sm={4} className="text-end">
              {loading && <Spinner animation="border" size="sm" style={{ color: "#7c3aed" }} />}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Services Grid (Card UI Layout matching reference design) */}
      {filteredServices.length > 0 ? (
        <Row className="g-4">
          {filteredServices.map((item, index) => {
            const price = parseFloat(item.amount || 0);
            const disc = parseFloat(item.discount || 0);
            const finalPrice = price - (price * disc) / 100;
            const displayPlanName =
              plansMap[item.website_plan] || item.plan_name || item.website_plan || "General Plan";
            const isActive = String(item.status) === "true";
            const isPurchased = purchasedIds.includes(String(item.id));

            return (
              <Col key={item.id || index} xl={4} lg={6} md={6} sm={12}>
                <Card
                  className="h-100 shadow-sm border-0 position-relative transition-all"
                  style={{ borderRadius: "20px", overflow: "hidden", background: "#ffffff" }}
                >
                  <Card.Body className="d-flex flex-column p-4">
                    {/* Header: Plan Badge & Status */}
                    <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                      <span
                        className="px-3 py-1 fw-semibold fs-7 text-truncate"
                        style={{
                          backgroundColor: "#dbeafe",
                          color: "#3b82f6",
                          borderRadius: "12px",
                          maxWidth: "60%",
                        }}
                        title={displayPlanName}
                      >
                        <i className="iconoir-page me-1"></i> {displayPlanName}
                      </span>
                      <span
                        className="px-3 py-1 fw-bold fs-7 text-white flex-shrink-0"
                        style={{
                          backgroundColor: isPurchased ? "#10b981" : isActive ? "#15803d" : "#6b7280",
                          borderRadius: "12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isPurchased ? "Purchased" : isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    {/* Service Icon & Title */}
                    <div className="d-flex align-items-center mb-2">
                      <div
                        className="rounded-3 d-inline-flex align-items-center justify-content-center me-3 flex-shrink-0"
                        style={{ width: 44, height: 44, backgroundColor: "#e0e7ff", color: "#6366f1" }}
                      >
                        <i className="iconoir-box fs-4"></i>
                      </div>
                      <h3 className="fw-bold text-dark mb-0">{item.name}</h3>
                    </div>

                    {/* Description */}
                    <p className="text-muted small mb-3 flex-grow-1" style={{ minHeight: "36px" }}>
                      {item.description || (
                        <>
                          Add-on paid service configured for <strong>{displayPlanName}</strong>.
                        </>
                      )}
                    </p>

                    {/* Price Section Box (matching design background #e7e2d9 / #f3ece1) */}
                    <div
                      className="p-3 rounded-4 mb-3 mt-auto"
                      style={{ backgroundColor: "#e6e0d6", border: "1px solid #dcd5c7" }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-muted small">Base Price:</span>
                        <span className={`small ${disc > 0 ? "text-decoration-line-through text-muted" : "fw-semibold text-dark"}`}>
                          ₹{price.toFixed(2)}
                        </span>
                      </div>

                      {disc > 0 && (
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="text-muted small">Discount Offer:</span>
                          <span
                            className="px-2 py-0.5 fw-bold text-dark fs-7"
                            style={{ backgroundColor: "#facc15", borderRadius: "6px" }}
                          >
                            {disc}% OFF
                          </span>
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center pt-2 border-top border-secondary-subtle mt-2">
                        <span className="fw-bold text-dark fs-6">Final Payable:</span>
                        <span className="fw-extrabold fs-4" style={{ color: "#047857" }}>
                          ₹{finalPrice.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* Action Button: Changes to "Purchased" after buying */}
                    {isPurchased ? (
                      <Button
                        disabled
                        className="w-100 py-2.5 fw-bold text-white shadow-sm border-0 d-flex align-items-center justify-content-center"
                        style={{
                          backgroundColor: "#10b981",
                          borderRadius: "14px",
                          fontSize: "1rem",
                        }}
                      >
                        <i className="iconoir-check-circle me-2 fs-5"></i> Purchased
                      </Button>
                    ) : (
                      <Button
                        disabled={!isActive}
                        className="w-100 py-2.5 fw-bold text-white shadow-sm border-0 d-flex align-items-center justify-content-center"
                        onClick={() => handleOpenPurchase(item)}
                        style={{
                          backgroundColor: isActive ? "#8063f5" : "#9ca3af",
                          borderRadius: "14px",
                          fontSize: "1rem",
                        }}
                      >
                        <i className="iconoir-shopping-bag me-2 fs-5"></i>
                        {isActive ? "Purchase Now" : "Unavailable"}
                      </Button>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <Card className="shadow-sm border-0 py-5 text-center" style={{ borderRadius: "20px" }}>
          <Card.Body>
            <div
              className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-3 text-muted"
              style={{ width: 64, height: 64 }}
            >
              <i className="iconoir-box fs-2"></i>
            </div>
            <h5 className="text-dark fw-bold">No Services Found</h5>
            <p className="text-muted small mb-0">
              {loading ? "Loading available services..." : "No paid services match your filter criteria."}
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Confirmation Modal */}
      <Modal
        show={showPurchaseModal}
        onHide={() => !purchasing && setShowPurchaseModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!purchasing} style={{ backgroundColor: "#8063f5" }} className="text-white py-3">
          <Modal.Title className="fs-6 text-white m-0 d-flex align-items-center">
            <i className="iconoir-shopping-bag me-2"></i> Confirm Service Purchase
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="py-4">
          {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

          {selectedService && (
            <div>
              <h4 className="fw-bold text-dark mb-1">{selectedService.name}</h4>
              <p className="text-muted small mb-3">
                Plan: <strong>{plansMap[selectedService.website_plan] || selectedService.plan_name || "General Plan"}</strong>
              </p>

              <div className="p-3 rounded-4 mb-3" style={{ backgroundColor: "#e6e0d6", border: "1px solid #dcd5c7" }}>
                <div className="d-flex justify-content-between mb-1 small text-muted">
                  <span>Base Service Fee:</span>
                  <span>₹{parseFloat(selectedService.amount || 0).toFixed(2)}</span>
                </div>
                {parseFloat(selectedService.discount || 0) > 0 && (
                  <div className="d-flex justify-content-between mb-1 small text-success">
                    <span>Discount ({selectedService.discount}%):</span>
                    <span>
                      -₹
                      {(
                        (parseFloat(selectedService.amount || 0) * parseFloat(selectedService.discount || 0)) /
                        100
                      ).toFixed(2)}
                    </span>
                  </div>
                )}
                <hr className="my-2" />
                <div className="d-flex justify-content-between fw-bold fs-6 text-dark">
                  <span>Total Payable:</span>
                  <span className="fw-extrabold fs-4" style={{ color: "#047857" }}>
                    ₹{selectedService.finalPrice}
                  </span>
                </div>
              </div>

              <p className="text-muted small mb-0">
                Clicking confirm will process the purchase for <strong>{selectedService.name}</strong> from your Main Wallet.
              </p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="py-2">
          <Button
            variant="light"
            size="sm"
            disabled={purchasing}
            onClick={() => setShowPurchaseModal(false)}
            style={{ borderRadius: "10px" }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={purchasing}
            onClick={handleConfirmPurchase}
            style={{ backgroundColor: "#8063f5", borderColor: "#8063f5", borderRadius: "10px", color: "#fff", fontWeight: "600" }}
          >
            {purchasing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing Purchase...
              </>
            ) : (
              "Confirm & Purchase"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal
        show={purchaseSuccess}
        onHide={() => setPurchaseSuccess(false)}
        centered
      >
        <Modal.Header closeButton className="bg-success text-white py-2">
          <Modal.Title className="fs-6 text-white m-0">Purchase Successful!</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div
            className="rounded-circle bg-success-subtle text-success d-inline-flex align-items-center justify-content-center mb-3"
            style={{ width: 56, height: 56 }}
          >
            <i className="iconoir-check-circle fs-2 text-success"></i>
          </div>
          <h5 className="fw-bold text-dark mb-2">Service Purchased!</h5>
          <p className="text-muted small mb-3">
            You have successfully purchased <strong>{successData?.service_name}</strong>.
          </p>
          <div className="bg-light p-3 rounded text-start small mb-3">
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Transaction ID:</span>
              <span className="fw-semibold text-dark">{successData?.transaction_id}</span>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Amount Paid:</span>
              <span className="fw-bold text-success">₹{successData?.amount_paid}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted">Date:</span>
              <span className="text-dark">{successData?.purchase_date}</span>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2 justify-content-center">
          <Button variant="success" size="sm" onClick={() => setPurchaseSuccess(false)}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PurchaseServices;
